import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeaderProps {
    title: ReactNode
    description?: ReactNode
    action?: ReactNode
    id?: string
    className?: string
}

export const SectionHeader: FC<SectionHeaderProps> = ({
    title,
    description,
    action,
    id,
    className
}): ReactNode => {
    return (
        <div className={cn('mb-3 flex items-end justify-between gap-3', className)}>
            <div className='min-w-0'>
                <h2 id={id} className='font-heading text-[17px] leading-tight font-bold text-foreground'>
                    {title}
                </h2>

                {description && (
                    <p className='mt-0.5 text-[13px] text-muted-foreground'>
                        {description}
                    </p>
                )}
            </div>

            {action && (
                <div className='shrink-0'>
                    {action}
                </div>
            )}
        </div>
    )
}

export const Card: FC<{ children: ReactNode, className?: string }> = ({ children, className }): ReactNode => {
    return (
        <div className={cn('rounded-2xl border border-border bg-card shadow-card', className)}>
            {children}
        </div>
    )
}

interface DetailRowProps {
    label: ReactNode
    children: ReactNode
    icon?: ReactNode
    className?: string
}

export const DetailRow: FC<DetailRowProps> = ({
    label,
    children,
    icon,
    className
}): ReactNode => {
    return (
        <div className={cn('flex items-center gap-3 py-3', className)}>
            {icon && (
                <span className='grid size-9 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-600 [&_svg]:size-[18px]'>
                    {icon}
                </span>
            )}

            <div className='min-w-0 flex-1 text-sm text-muted-foreground'>
                {label}
            </div>

            <div className='min-w-0 text-right text-sm font-semibold text-foreground'>
                {children}
            </div>
        </div>
    )
}
